/**
 * Powerlink configurator: step selection -> live summary -> copy / scroll-to-contact.
 *
 * Self-contained (no dependency on theme.js custom elements) since this configurator does not touch
 * product/variant/cart state — Powerlink is sold via quote request, so there is no variant to select
 * or /cart/add.js call to make here.
 *
 * Steps can be single-select (radio, data-pwlc-step without data-pwlc-multi) or multi-select
 * (checkbox, data-pwlc-multi="true") — e.g. storage trays can be combined, so that step is multi.
 * An option can carry data-notice with a short safety/info text (e.g. the Slim-depth anchoring
 * requirement); any notices from currently selected options are shown above the summary.
 *
 * Incompatible options: the root carries data-incompatible with "id:id" pairs of option block IDs
 * (section setting "incompatible_pairs", e.g. "option_1_3:option_2_2" = Smith Multipower + Slim depth).
 * Selecting one option disables its incompatible partners (dimmed, not selectable, with a translated
 * hint), so a forbidden combination can never reach the summary or the quote request. If a forbidden
 * combination is already checked (e.g. browser form-state restore), the later option in page order
 * is unchecked.
 *
 * The "notices" heading used in the copied/sent summary comes from data-notices-label on the root
 * (translated per locale via snippets/sw-t.liquid), so the summary follows the storefront language.
 *
 * On mobile, each step's options row becomes a horizontal scroll-snap carousel (pure CSS, native
 * touch swipe) so a step with many options doesn't cram into a tiny multi-column grid.
 *
 * The HubSpot contact form below is embedded in a same-origin iframe (HubSpot writes the form
 * directly into an about:blank frame rather than loading it from its own domain), so its fields are
 * reachable from here — findContactMessageField()/fillContactMessage() push the summary straight into
 * the "Cuéntanos más" textarea when the CTA is clicked, instead of relying on manual copy/paste.
 */
(function () {
  'use strict';

  /* "a:b, c:d" -> { a: ['b'], b: ['a'], c: ['d'], d: ['c'] } (symmetric) */
  function parsePairs(raw) {
    var map = {};
    (raw || '').split(',').forEach(function (pair) {
      var ids = pair.split(':').map(function (s) { return s.trim(); }).filter(Boolean);
      if (ids.length !== 2) return;
      (map[ids[0]] = map[ids[0]] || []).push(ids[1]);
      (map[ids[1]] = map[ids[1]] || []).push(ids[0]);
    });
    return map;
  }

  function initConfigurator(root) {
    if (root.dataset.pwlcInit) return;
    root.dataset.pwlcInit = 'true';

    var steps = Array.prototype.slice.call(root.querySelectorAll('[data-pwlc-step]'));
    var allInputs = Array.prototype.slice.call(root.querySelectorAll('[data-pwlc-input]'));
    var summaryList = root.querySelector('[data-pwlc-summary-list]');
    var summaryEmpty = root.querySelector('[data-pwlc-summary-empty]');
    var summaryActions = root.querySelector('[data-pwlc-summary-actions]');
    var summaryNotices = root.querySelector('[data-pwlc-summary-notices]');
    var copyButton = root.querySelector('[data-pwlc-copy]');
    var copyFeedback = root.querySelector('[data-pwlc-copy-feedback]');
    var gotoContactButton = root.querySelector('[data-pwlc-goto-contact]');
    var noticesLabel = root.getAttribute('data-notices-label') || 'Avisos';
    var incompatible = parsePairs(root.getAttribute('data-incompatible'));

    /* stepIndex -> { title, values: [{ value, notice }] } */
    var selections = {};

    /**
     * Disables options that conflict with a checked option and unchecks any conflicting option
     * already checked (later in page order loses). Returns the steps whose selection changed.
     */
    function applyIncompatibilities() {
      var touchedSteps = [];
      var blocked = {};

      allInputs.forEach(function (input) {
        if (!input.checked) return;
        var id = input.getAttribute('data-option-id');
        if (blocked[id]) {
          input.checked = false;
          var step = input.closest('[data-pwlc-step]');
          if (step && touchedSteps.indexOf(step) === -1) touchedSteps.push(step);
          return;
        }
        (incompatible[id] || []).forEach(function (other) { blocked[other] = true; });
      });

      allInputs.forEach(function (input) {
        var off = !input.checked && !!blocked[input.getAttribute('data-option-id')];
        input.disabled = off;
        var option = input.closest('.pwlc-option');
        if (!option) return;
        option.classList.toggle('is-incompatible', off);
        var hint = option.querySelector('[data-pwlc-incompat]');
        if (hint) hint.hidden = !off;
      });

      return touchedSteps;
    }

    function collectNotices() {
      var notices = [];
      steps.forEach(function (step) {
        var stepIndex = step.getAttribute('data-pwlc-step');
        var entry = selections[stepIndex];
        if (!entry) return;
        entry.values.forEach(function (v) {
          if (v.notice) notices.push(v.notice);
        });
      });
      return notices;
    }

    function renderSummary() {
      var entries = steps
        .map(function (step) {
          var stepIndex = step.getAttribute('data-pwlc-step');
          return selections[stepIndex];
        })
        .filter(function (entry) {
          return entry && entry.values.length;
        });

      if (!entries.length) {
        summaryEmpty.hidden = false;
        summaryList.hidden = true;
        summaryActions.hidden = true;
        if (summaryNotices) summaryNotices.hidden = true;
        return;
      }

      summaryEmpty.hidden = true;
      summaryList.hidden = false;
      summaryActions.hidden = false;

      summaryList.innerHTML = '';

      entries.forEach(function (entry) {
        var item = document.createElement('li');

        var stepLabel = document.createElement('span');
        stepLabel.className = 'pwlc-summary-step';
        stepLabel.textContent = entry.title;

        var valueLabel = document.createElement('span');
        valueLabel.className = 'pwlc-summary-value';
        valueLabel.textContent = entry.values.map(function (v) { return v.value; }).join(', ');

        item.appendChild(stepLabel);
        item.appendChild(valueLabel);
        summaryList.appendChild(item);
      });

      if (summaryNotices) {
        var notices = collectNotices();
        if (notices.length) {
          summaryNotices.innerHTML = '';
          notices.forEach(function (text) {
            var p = document.createElement('p');
            p.className = 'pwlc-notice';
            p.textContent = text;
            summaryNotices.appendChild(p);
          });
          summaryNotices.hidden = false;
        } else {
          summaryNotices.hidden = true;
        }
      }
    }

    function summaryText() {
      var lines = steps
        .map(function (step) {
          var stepIndex = step.getAttribute('data-pwlc-step');
          var entry = selections[stepIndex];
          if (!entry || !entry.values.length) return null;
          return entry.title + ': ' + entry.values.map(function (v) { return v.value; }).join(', ');
        })
        .filter(Boolean);

      var notices = collectNotices();
      if (notices.length) {
        lines.push('');
        lines.push(noticesLabel + ':');
        notices.forEach(function (text) { lines.push('- ' + text); });
      }

      return lines.join('\n');
    }

    function updateStepSelection(step) {
      var stepIndex = step.getAttribute('data-pwlc-step');
      var title = step.getAttribute('data-step-title') || '';
      var inputs = Array.prototype.slice.call(step.querySelectorAll('[data-pwlc-input]'));

      var checked = inputs.filter(function (input) { return input.checked; });

      if (!checked.length) {
        delete selections[stepIndex];
        renderSummary();
        return;
      }

      selections[stepIndex] = {
        title: title,
        values: checked.map(function (input) {
          return { value: input.value, notice: input.getAttribute('data-notice') || '' };
        })
      };

      renderSummary();
    }

    root.addEventListener('change', function (event) {
      var input = event.target;
      if (!input || !input.matches('[data-pwlc-input]')) return;

      var touched = applyIncompatibilities();
      var step = input.closest('[data-pwlc-step]');
      if (step && touched.indexOf(step) === -1) touched.push(step);
      touched.forEach(updateStepSelection);
    });

    if (copyButton) {
      copyButton.addEventListener('click', function () {
        var text = summaryText();
        if (!text) return;

        function showCopied() {
          if (!copyFeedback) return;
          copyFeedback.hidden = false;
          window.setTimeout(function () {
            copyFeedback.hidden = true;
          }, 2000);
        }

        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(text).then(showCopied, function () {
            /* clipboard permission denied or unavailable: silently ignore, user can still read the summary on screen */
          });
        }
      });
    }

    function findContactMessageField() {
      var iframes = Array.prototype.slice.call(document.querySelectorAll('iframe[id^="hs-form-iframe"]'));
      for (var i = 0; i < iframes.length; i++) {
        var doc;
        try {
          doc = iframes[i].contentDocument;
        } catch (e) {
          continue; /* genuinely cross-origin frame — can't reach it, skip */
        }
        if (!doc) continue;
        var field = doc.querySelector('textarea[name="message"]');
        if (field) return field;
      }
      return null;
    }

    function fillContactMessage(text) {
      var field = findContactMessageField();
      if (!field) return;

      var frameWindow = field.ownerDocument.defaultView;
      var setter = Object.getOwnPropertyDescriptor(frameWindow.HTMLTextAreaElement.prototype, 'value').set;
      setter.call(field, text);
      field.dispatchEvent(new Event('input', { bubbles: true }));
      field.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (gotoContactButton) {
      gotoContactButton.addEventListener('click', function () {
        var text = summaryText();
        if (text) fillContactMessage(text);

        var contactForm = document.querySelector('.hs-form-frame, [data-hs-forms-root], form[id^="hsForm"]');
        var target = contactForm ? contactForm.closest('section') || contactForm : null;

        if (target) {
          var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          target.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' });
        }
      });
    }

    /* Initial pass: covers options restored as checked by the browser (back/forward cache). */
    applyIncompatibilities();
    steps.forEach(updateStepSelection);
  }

  function initAll() {
    document.querySelectorAll('.pwlc').forEach(initConfigurator);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();